import React, { memo, useMemo } from "react";
import { HStack, Text, VStack } from "@chakra-ui/react";
import colors from "app/colors";

import { CALLOUT_KEY } from "modules/utils/Constants";
import { DistributionGroupFragment, UserFragment } from "gql/graphql";
import { DynamicRulesIcon, GroupMembershipIcon } from "modules/icons";
import { RuleGroupType } from "react-querybuilder";
import useQueryBuilder from "modules/hooks/use-query-builder";

interface IProps {
  addedUsers: UserFragment[];
  distributionGroups: DistributionGroupFragment[];
  emailUsers: UserFragment[];
  query?: RuleGroupType;
}

const AdminDistributionGroupPreviewUsersAndGroups = ({
  addedUsers,
  distributionGroups,
  emailUsers,
  query,
}: IProps) => {
  const addedUserCount = addedUsers.length;

  const addedDistributionGroupCount = distributionGroups.length;

  const { naturalLanguageQuery } = useQueryBuilder({
    query,
  });

  const groupNames = useMemo(
    () =>
      distributionGroups
        .map((distributionGroup) => `${CALLOUT_KEY}${distributionGroup.name}`)
        .join(", "),
    [distributionGroups],
  );

  const header = useMemo(() => {
    if (addedUserCount > 0 && addedDistributionGroupCount > 0) {
      return `Users (${addedUserCount}) & Groups (${addedDistributionGroupCount}): `;
    }
    if (addedUserCount > 0) {
      return `Users (${addedUserCount}): `;
    }
    if (addedDistributionGroupCount > 0) {
      return `Groups (${addedDistributionGroupCount}): `;
    }
    return null;
  }, [addedUserCount, addedDistributionGroupCount]);

  if (
    addedUserCount === 0 &&
    addedDistributionGroupCount === 0 &&
    emailUsers &&
    !query
  ) {
    return null;
  }

  return (
    <VStack bg={colors.whiteTertiary} w="full" p="6px">
      {groupNames.length && (
        <HStack w="full">
          <GroupMembershipIcon boxSize={4} />
          <Text fontWeight={700} fontSize={14} color={colors.gray50}>
            {header}
            <Text as="span" fontWeight={500}>
              {groupNames}
            </Text>
          </Text>
        </HStack>
      )}
      {naturalLanguageQuery && (
        <HStack w="full" alignItems="flex-start">
          <DynamicRulesIcon boxSize={4} />
          <Text fontWeight={700} fontSize={14} color={colors.gray50}>
            Dynamic Rules:
            <Text as="span" fontWeight={500}>
              {naturalLanguageQuery}
            </Text>
          </Text>
        </HStack>
      )}
    </VStack>
  );
};

export default memo(AdminDistributionGroupPreviewUsersAndGroups);
